import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { IResidentProfileCommon } from './../../core/store/resident-profile/resident-profile.interfaces';

import { IAuthCommon } from 'src/app/auth/store/auth.interfaces';
import { StrataPlanRequest } from '../models/action-request';
import { ProfileUpdate } from '../models/profile';
import { SiteSettings } from '../models/site-settings';
import { StrataResponse } from './../models/strata-response';

@Injectable({
  providedIn: 'root',
})
export class ResidentProfileHttpService {
  constructor(private http: HttpClient) {}

  public loadUserProfile(): Observable<IResidentProfileCommon> {
    return this.http
      .get<any>(`${environment.api.root}/${environment.api.resident_profile}`)
      .pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  public fetchLotsAndUnits(webAddressName: string): Observable<StrataResponse> {
    return this.http
      .get<any>(
        `${environment.api.root}/${environment.api.strata_detail}/${webAddressName}`
      )
      .pipe(
        map((res) => {
          if (res) {
            return res.data;
          }
          return null;
        })
      );
  }

  public fetchLotsAndUnitsWithStrataPlan(): Observable<StrataResponse> {
    return this.http
      .get<any>(`${environment.api.root}/${environment.api.strata_detail}`)
      .pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  public getSiteSettings(reqObj: StrataPlanRequest): Observable<SiteSettings> {
    return this.http
      .post<any>(
        `${environment.api.root}/${environment.api.site_settings}`,
        reqObj
      )
      .pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  public getPropertyLogo(reqObj: StrataPlanRequest): Observable<any> {
    if (reqObj.strataPlanNumber === '') {
      return of(false);
    } else {
      return this.http
        .post<any>(
          `${environment.api.root}/${environment.ui.strata_body_corporate}/${environment.ui.property_logo}/strata-plan-number`,
          reqObj
        )
        .pipe(
          map((res) => {
            return res.data;
          })
        );
    }
  }

  public validateEmail(email: string): Observable<boolean> {
    return this.http
      .get<any>(
        `${environment.api.root}/${environment.api.validate_email}/${email}`
      )
      .pipe(
        map((res) => {
          return res.data === true;
        })
      );
  }

  public validateUsername(username: string): Observable<boolean> {
    return this.http
      .get<any>(
        `${environment.api.root}/${environment.api.validate_username}/${username}`
      )
      .pipe(
        map((res) => {
          return res.data === true;
        })
      );
  }

  public updateProfile(profileUpdate: ProfileUpdate): Observable<any> {
    const formData = new FormData();
    formData.append('profileId', profileUpdate.profileId.toString());
    formData.append('displayName', profileUpdate.displayName ?? '');
    formData.append('email', profileUpdate.email ?? '');
    formData.append('title', profileUpdate.title ?? '');
    formData.append('facebookProfile', profileUpdate.facebookProfile ?? '');
    formData.append('twitterHandle', profileUpdate.twitterHandle ?? '');
    formData.append('instagramProfile', profileUpdate.instagramProfile ?? '');
    formData.append('mobilePhone', profileUpdate.mobilePhone ?? '');
    formData.append('phone', profileUpdate.phone ?? '');
    formData.append('receiveNotificationEmail', profileUpdate.receiveNotificationEmail?.toString() ?? 'false');
    formData.append('receiveNotificationSms', profileUpdate.receiveNotificationSms?.toString() ?? 'false');
    formData.append('profilePicture', profileUpdate.picture);

    return this.http
      .put<any>(
        `${environment.api.root}/${environment.api.update_resident_profile}`,
        formData
      )
      .pipe(map((res) => res.data));
  }

  public changeProfile(profileId: number): Observable<IAuthCommon> {
    const data = { profileId };
    return this.http.post<IAuthCommon>(
      `${environment.api.root}/${environment.api.change_profile}`,
      { data }
    );
  }
}
